/**
 * Routine builder + premake API. The accountant authors routines here; the runner
 * premakes UNPOSTED drafts + Darbai cards (nothing posts without the human confirm).
 */
import { api } from './client';

export type RoutineKind = 'PayrollAccrual' | 'RecurringInvoice';
export type RoutineCadence = 'Monthly' | 'Weekly' | 'Annually';
export type RoutineStatus = 'Active' | 'Paused';
export type RoutinePostingRule = 'MonthEnd' | 'TriggerDay';

export interface RoutineDto {
  id: number;
  kind: RoutineKind | string;
  status: RoutineStatus | string;
  name: string;
  cadence: RoutineCadence | string;
  triggerDayOfMonth: number;
  triggerDayOfWeek: number | null;
  targetPeriodOffset: number;
  postingDateRule: RoutinePostingRule | string;
  templateJson: string;
  lastPremadePeriod: string | null;
  lastPremadeJournalEntryId: number | null;
  lastDraftPosted: boolean;
  overdueAfterDays: number;
}

export interface RoutineSuggestionDto {
  kind: string;
  title: string;
  evidenceSummary: string;
  proposedTemplateJson: string;
  proposedTriggerDay: number;
  proposedPeriodOffset: number;
  proposedPostingRule: string;
}

export interface PremakeResultDto {
  created: boolean;
  journalEntryId: number | null;
  alarmCode: string | null;
  error: string | null;
}

export const routinesApi = {
  list: () => api.get<RoutineDto[]>('/routines'),
  suggestions: () => api.get<RoutineSuggestionDto[]>('/routines/suggestions'),
  create: (request: {
    kind: string; name: string; triggerDayOfMonth: number; targetPeriodOffset: number;
    postingDateRule: string; templateJson: string; overdueAfterDays?: number;
    cadence?: string; triggerDayOfWeek?: number | null;
  }) => api.post<RoutineDto>('/routines', request),
  update: (id: number, request: {
    name: string; triggerDayOfMonth: number; targetPeriodOffset: number;
    postingDateRule: string; templateJson: string; overdueAfterDays: number;
  }) => api.put<RoutineDto>(`/routines/${id}`, request),
  pause: (id: number) => api.put<RoutineDto>(`/routines/${id}/pause`),
  resume: (id: number) => api.put<RoutineDto>(`/routines/${id}/resume`),
  remove: (id: number) => api.delete(`/routines/${id}`),
  run: () => api.post<{ routinesChecked: number; draftsPremade: number; findingsEmitted: number; alarms: number }>('/routines/run'),
  premake: (id: number, period?: string) =>
    api.post<PremakeResultDto>(`/routines/${id}/premake${period ? `?period=${period}` : ''}`),
  catchup: (id: number) =>
    api.post<{ draftsCreated: number; drafts: { period: string; journalEntryId: number; amount: number }[]; error: string | null }>(`/routines/${id}/catchup`),
};
